import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Type } from '@spotify-internal/encore-creator-web';
import styles from './index.module.scss';
import {
  LOADING,
  ERROR,
  selectNetworkListeners,
  selectNetworkListenersStatus,
  selectDateRange,
} from '../../../store/selectors';
import { loadNetworkListeners } from '../../../store/networkListeners';
import { PerformanceOverTimeChart } from '../../../components/Shared/Charts/PerformanceOverTimeChart';
import { LoadingIndicator } from '../../../components/Shared/LoadingIndicator';
import { Listener, DateRangeWithLabel, ReduxState } from '../../../types';
import { FormattedMessage, IntlShape, injectIntl, defineMessage } from 'react-intl';

type Props = {
  networkId: string;
  status?: 'LOADING' | 'ERROR';
  listeners?: Listener[];
  loadNetworkListeners: typeof loadNetworkListeners;
  dateRange?: DateRangeWithLabel;
  intl: IntlShape;
};

class InternationalizedNetworkListeners extends PureComponent<Props> {
  componentDidMount() {
    this.props.loadNetworkListeners(this.props.networkId);
  }

  /* istanbul ignore next */
  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (!this.props.dateRange || !nextProps.dateRange) {
      return;
    }

    if (
      nextProps.dateRange.startDate !== this.props.dateRange.startDate ||
      nextProps.dateRange.endDate !== this.props.dateRange.endDate
    ) {
      this.props.loadNetworkListeners(this.props.networkId);
    }
  }

  render() {
    const { status, listeners, intl } = this.props;
    const chartMsg = defineMessage({
      id: 'podcast.audience.listeners.tooltip-listeners',
      defaultMessage: 'Listeners',
    });
    const name = intl.formatMessage(chartMsg);

    if (status === ERROR) {
      return <span data-qa="network-listeners-error" />;
    }

    if (!listeners && status !== LOADING) {
      return null;
    }

    return (
      <div data-qa="network-listeners-chart" className={styles.container}>
        <Type as="p" variant={Type.heading3}>
          <FormattedMessage
            id="network.audience.listeners.title"
            description="Title of listeners chart"
            defaultMessage="Listeners"
          />
        </Type>
        {status === LOADING ? (
          <div style={{ width: '100%', height: '350px' }}>
            <LoadingIndicator />
          </div>
        ) : (
          <PerformanceOverTimeChart performanceData={this.props.listeners!} name={name} />
        )}
      </div>
    );
  }
}

const selectors = createStructuredSelector<
  ReduxState,
  {
    status: ReturnType<typeof selectNetworkListenersStatus>;
    listeners: ReturnType<typeof selectNetworkListeners>;
    dateRange: ReturnType<typeof selectDateRange>;
  }
>({
  status: selectNetworkListenersStatus,
  listeners: selectNetworkListeners,
  dateRange: selectDateRange,
});

const actions = {
  loadNetworkListeners,
};

const NetworkListeners = injectIntl(InternationalizedNetworkListeners);

export const ConnectedNetworkListeners = connect(selectors, actions)(NetworkListeners);
